import { Combobox as ACombobox } from '@ark-ui/solid/combobox';
import { IconX } from '@bbr/icons/x-outline';
import { For, Show, splitProps } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { cn, tv } from 'tailwind-variants';
import { Button } from './button.tsx';
import { Field } from './field.tsx';
import type { CollectionItem } from '@ark-ui/solid/combobox';
import type { IconComponent } from '@bbr/icons';
import type { Component, ComponentProps, JSXElement } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

function ComboboxItem(props: ComponentProps<typeof ACombobox.Item>) {
	return (
		<ACombobox.Item
			item={props.item}
			class="rounded px-2 py-1 transition-colors data-disable:text-zinc-400 data-highlighted:bg-zinc-300 dark:data-highlighted:bg-zinc-700"
		>
			<ACombobox.ItemText>{props.item.label}</ACombobox.ItemText>
		</ACombobox.Item>
	);
}

export function Combobox<T>(
	props: ComponentProps<typeof ACombobox.Root<T>> & {
		name: string;
		ItemComponent?: Component<ComponentProps<typeof ComboboxItem>>;
	},
) {
	return (
		<Combobox.Root {...props} class="flex w-full flex-col justify-start gap-1">
			{props.children}
			<ACombobox.Positioner>
				<ACombobox.Content class="max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) rounded-lg border border-zinc-300 bg-zinc-50/75 p-2 shadow-md backdrop-blur dark:border-zinc-700 dark:bg-zinc-950/74">
					<div class="flex max-h-[min(calc(var(--available-height)-1rem),16rem)] flex-col gap-2 overflow-y-scroll overscroll-contain" />
					<For each={props.collection.items}>
						{(item) => <Dynamic component={props.ItemComponent ?? ComboboxItem} item={item} />}
					</For>
				</ACombobox.Content>
			</ACombobox.Positioner>
		</Combobox.Root>
	);
}

Combobox.Root = <T extends CollectionItem>(props: ComponentProps<typeof ACombobox.Root<T>> & { name?: string }) => (
	<Field name={props.name ?? ''}>
		<ACombobox.Root {...props} class="flex w-full flex-col justify-start gap-1" />
	</Field>
);

Combobox.Positioner = ACombobox.Positioner;
Combobox.Content = (
	props: Omit<ComponentProps<typeof ACombobox.Content>, 'class'> & VariantProps<typeof contentClass>,
) => <ACombobox.Content {...props} class={contentClass({ portal: props.portal, rounding: props.rounding })} />;

const contentClass = tv({
	base: 'z-50 flex max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) flex-col gap-4 overflow-y-auto overscroll-contain',
	variants: {
		portal: {
			true: 'border border-zinc-300 bg-zinc-50/85 p-2 shadow-md backdrop-blur dark:border-zinc-700 dark:bg-zinc-900/85',
			false: '',
		},
		rounding: {
			md: 'rounded-lg',
			full: 'rounded-2xl',
		},
	},
	defaultVariants: {
		rounding: 'md',
		portal: true,
	},
});

Combobox.Empty = ACombobox.Empty;
Combobox.Trigger = ACombobox.Trigger;
Combobox.ClearTrigger = ACombobox.ClearTrigger;
Combobox.ClearTriggerButton = () => (
	<Combobox.ClearTrigger
		asChild={(p) => (
			// @ts-expect-error spread makes too complex
			<Button
				{...p()}
				as="button"
				size="md"
				rounding="circle"
				appearance="subtle"
				icon={IconX}
				iconPosition="only"
				iconLabel="Clear"
			/>
		)}
	/>
);
Combobox.ItemGroup = (props: ComponentProps<typeof ACombobox.ItemGroup>) => (
	<ACombobox.ItemGroup {...props} class="flex flex-col gap-2" />
);
Combobox.ItemGroupLabel = (props: ComponentProps<typeof ACombobox.ItemGroupLabel>) => (
	<ACombobox.ItemGroupLabel
		{...props}
		class="flex items-center gap-1 px-2 text-sm font-semibold text-zinc-600 dark:text-zinc-400"
	/>
);

Combobox.Label = (props: ComponentProps<typeof ACombobox.Label>) => (
	<ACombobox.Label {...props} class={cn('line-clamp-2 text-lg font-medium', props.class)} />
);

Combobox.Control = (
	props: ComponentProps<typeof ACombobox.Input> & { icon?: IconComponent } & VariantProps<typeof controlClass> &
		VariantProps<typeof inputClass>,
) => {
	const [, spread] = splitProps(props, ['size', 'children', 'rounding', 'icon']);
	return (
		<ACombobox.Control class={controlClass({ rounding: props.rounding })}>
			<Dynamic component={props.icon} />
			<ACombobox.Input {...spread} class={inputClass({ size: props.size })} />
			{props.children}
		</ACombobox.Control>
	);
};

const controlClass = tv({
	base: 'relative grid w-full grid-cols-[min-content_1fr_min-content] items-center justify-between gap-x-1 rounded-md border border-zinc-300 bg-white ps-3 pe-0.5 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 has-focus-visible:ring-4 has-focus-visible:ring-yellow-500/50 has-focus-visible:ring-offset-1 has-focus-visible:ring-offset-zinc-50 has-disabled:bg-zinc-100 has-disabled:text-zinc-500 has-aria-invalid:border-rose-600 has-aria-readonly:cursor-not-allowed has-aria-readonly:bg-zinc-100 has-aria-readonly:text-zinc-500 motion-safe:has-disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:has-focus-visible:ring-offset-zinc-950 dark:has-disabled:bg-zinc-800',
	variants: {
		rounding: {
			md: 'rounded-md',
			full: 'rounded-full',
		},
	},
	defaultVariants: {
		rounding: 'md',
	},
});

const inputClass = tv({
	base: 'min-w-0 shrink grow outline-none',
	variants: {
		size: {
			md: 'py-2',
			lg: 'py-3 text-xl',
		},
	},
	defaultVariants: {
		size: 'md',
	},
});

Combobox.Item = ACombobox.Item;
Combobox.ItemText = ACombobox.ItemText;
Combobox.ItemIndicator = ACombobox.ItemIndicator;

export function ComboboxField<T>(
	props: ComponentProps<typeof Combobox<T>> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<Combobox {...rest}>
			<Combobox.Label>{other.label}</Combobox.Label>
			<Combobox.Control />
			<Field.Errors />
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Combobox>
	);
}

export { useAsyncList } from '@ark-ui/solid/collection';
export { createListCollection } from '@ark-ui/solid/combobox';
